import api from "../utils/api";
import useFlashMessage from "./useFlashMessages";
import { useNavigate } from "react-router-dom";
import { useEffect, useCallback } from "react";
import { CHAVE_TOKEN } from "../utils/formata";

// Hook único com todas as chamadas do sistema de crédito
export default function useHelp() {
  const { setFlashMessage } = useFlashMessage();
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem(CHAVE_TOKEN);
    if (token) {
      api.defaults.headers.Authorization = `Bearer ${token}`;
    }
  }, []);

  function trataSucesso(response, rotaNavegar) {
    setFlashMessage(response.data.message, "success");
    if (rotaNavegar) navigate(rotaNavegar);
    return response.data;
  }

  // ===== Dashboard =====
  const consultarDashboard = useCallback(async () => {
    return api.get("/dashboard").then((r) => r.data);
  }, []);

  const consultarRelatorioDiario = useCallback(async (data) => {
    return api
      .get("/dashboard/relatorio-diario", { params: { data } })
      .then((r) => r.data);
  }, []);

  // ===== Empresas =====
  const consultarEmpresas = useCallback(async () => {
    return api.get("/empresa").then((r) => r.data.empresas);
  }, []);

  const consultarEmpresa = useCallback(async (id) => {
    return api.get(`/empresa/${id}`).then((r) => r.data.empresa);
  }, []);

  const adicionarEmpresa = useCallback((empresa) => {
    return api
      .post("/empresa/add", empresa)
      .then((r) => trataSucesso(r, "/empresas"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Só as observações (onde mora a frequência da cobrança em lote), sem sair da tela.
  // A rota de edição exige razão social e responsável — vão os que já estão no cadastro.
  const salvarObsEmpresa = useCallback((empresa, obs) => {
    return api
      .post("/empresa/add", { id: empresa.id, razaoSocial: empresa.razaoSocial, responsavel: empresa.responsavel, obs })
      .then((r) => trataSucesso(r));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const removerEmpresa = useCallback((id) => {
    return api.delete(`/empresa/${id}`).then((r) => {
      setFlashMessage(r.data.message, "success");
      return r.status;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ===== Empréstimos =====
  const simularEmprestimo = useCallback(async (dados) => {
    return api.post("/emprestimo/simular", dados).then((r) => r.data.cronograma);
  }, []);

  const consultarEmprestimos = useCallback(async () => {
    return api.get("/emprestimo").then((r) => r.data.emprestimos);
  }, []);

  const consultarEmprestimo = useCallback(async (id) => {
    return api.get(`/emprestimo/${id}`).then((r) => r.data.emprestimo);
  }, []);

  const adicionarEmprestimo = useCallback((emprestimo) => {
    return api
      .post("/emprestimo/add", emprestimo)
      .then((r) => trataSucesso(r, "/emprestimos"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const editarEmprestimo = useCallback((id, emprestimo) => {
    return api
      .put(`/emprestimo/${id}`, emprestimo)
      .then((r) => trataSucesso(r, `/emprestimo/${id}`));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const liberarEmprestimo = useCallback((id) => {
    return api.post(`/emprestimo/${id}/liberar`, {}).then((r) => trataSucesso(r));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const renovarEmprestimo = useCallback((idOrigem, dados) => {
    return api
      .post(`/emprestimo/${idOrigem}/renovar`, dados)
      .then((r) => trataSucesso(r, "/emprestimos"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ===== Atendimento com IA =====
  const consultarConhecimento = useCallback(async () => {
    return api.get("/ia/conhecimento").then((r) => r.data.itens);
  }, []);

  const salvarConhecimento = useCallback((item) => {
    return api.post("/ia/conhecimento", item).then((r) => trataSucesso(r));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const removerConhecimento = useCallback((id) => {
    return api.delete(`/ia/conhecimento/${id}`).then((r) => {
      setFlashMessage(r.data.message, "success");
      return r.status;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const consultarConfigIa = useCallback(async () => {
    return api.get("/ia/config").then((r) => r.data.config);
  }, []);

  const statusRobo = useCallback(async () => {
    return api.get("/robo/status").then((r) => r.data.robo);
  }, []);

  const escolherModoRobo = useCallback(async (modoConexao) => {
    return api.put("/robo/modo", { modoConexao }).then((r) => r.data);
  }, []);

  const salvarConfigIa = useCallback((cfg) => {
    return api.post("/ia/config", cfg).then((r) => trataSucesso(r));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ===== Visitas =====
  const consultarAgendaVisitas = useCallback(async () => {
    return api.get("/emprestimo/agenda/visitas").then((r) => r.data.visitas);
  }, []);

  const marcarVisita = useCallback((id, dados) => {
    return api.post(`/emprestimo/${id}/visita`, dados).then((r) => trataSucesso(r));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const desmarcarVisita = useCallback((id) => {
    return api.delete(`/emprestimo/${id}/visita`).then((r) => {
      setFlashMessage(r.data.message, "success");
      return r.status;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const assinarEmprestimo = useCallback((id, assinatura) => {
    return api.post(`/emprestimo/${id}/assinar`, assinatura).then((r) => trataSucesso(r));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const cancelarEmprestimo = useCallback((id) => {
    return api.post(`/emprestimo/${id}/cancelar`, {}).then((r) => trataSucesso(r));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const removerEmprestimo = useCallback((id) => {
    return api.delete(`/emprestimo/${id}`).then((r) => {
      setFlashMessage(r.data.message, "success");
      return r.status;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ===== Parcelas =====
  const consultarParcelasAbertas = useCallback(async (filtro, ate) => {
    return api
      .get("/parcela/abertas", { params: { filtro, ate } })
      .then((r) => r.data.parcelas);
  }, []);

  const pagarParcela = useCallback((id, dados) => {
    return api.post(`/parcela/${id}/pagar`, dados).then((r) => trataSucesso(r));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const gerarPixParcela = useCallback((id) => {
    return api.post(`/pix/parcela/${id}`, {}).then((r) => trataSucesso(r));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const estornarParcela = useCallback((id) => {
    return api.post(`/parcela/${id}/estornar`, {}).then((r) => trataSucesso(r));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ===== Movimentações (caixa) =====
  const consultarMovimentacoes = useCallback(async (params) => {
    return api.get("/movimentacao", { params }).then((r) => r.data);
  }, []);

  const listarDespesas = useCallback(async (mes) => {
    return api.get("/despesa", { params: mes ? { mes } : {} }).then((r) => r.data);
  }, []);

  const adicionarDespesa = useCallback((despesa) => {
    return api.post("/despesa/add", despesa).then((r) => trataSucesso(r));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const removerDespesa = useCallback((id) => {
    return api.delete(`/despesa/${id}`).then((r) => { setFlashMessage(r.data.message, "success"); return r.status; });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const lancarDespesasFixas = useCallback((mes) => {
    return api.post("/despesa/fixas/lancar", mes ? { mes } : {}).then((r) => trataSucesso(r));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const removerDespesaFixa = useCallback((id) => {
    return api.delete(`/despesa/fixa/${id}`).then((r) => { setFlashMessage(r.data.message, "success"); return r.status; });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const adicionarMovimentacao = useCallback((mov) => {
    return api.post("/movimentacao/add", mov).then((r) => trataSucesso(r));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const removerMovimentacao = useCallback((id) => {
    return api.delete(`/movimentacao/${id}`).then((r) => {
      setFlashMessage(r.data.message, "success");
      return r.status;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ===== Documentos =====
  const consultarDocumentos = useCallback(async (params) => {
    return api.get("/documento", { params }).then((r) => r.data.documentos);
  }, []);

  const adicionarDocumento = useCallback((formData) => {
    return api
      .post("/documento/add", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      .then((r) => trataSucesso(r));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const removerDocumento = useCallback((id) => {
    return api.delete(`/documento/${id}`).then((r) => {
      setFlashMessage(r.data.message, "success");
      return r.status;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    consultarDashboard,
    consultarRelatorioDiario,
    consultarEmpresas,
    consultarEmpresa,
    adicionarEmpresa,
    salvarObsEmpresa,
    removerEmpresa,
    simularEmprestimo,
    consultarEmprestimos,
    consultarEmprestimo,
    adicionarEmprestimo,
    editarEmprestimo,
    consultarConhecimento,
    salvarConhecimento,
    removerConhecimento,
    consultarConfigIa,
    statusRobo,
    escolherModoRobo,
    salvarConfigIa,
    consultarAgendaVisitas,
    marcarVisita,
    desmarcarVisita,
    liberarEmprestimo,
    renovarEmprestimo,
    assinarEmprestimo,
    cancelarEmprestimo,
    removerEmprestimo,
    consultarParcelasAbertas,
    pagarParcela,
    estornarParcela,
    gerarPixParcela,
    consultarMovimentacoes,
    adicionarMovimentacao,
    removerMovimentacao,
    listarDespesas,
    adicionarDespesa,
    removerDespesa,
    lancarDespesasFixas,
    removerDespesaFixa,
    consultarDocumentos,
    adicionarDocumento,
    removerDocumento,
  };
}
